import { useMemo } from 'react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { useScenarioCoverageProgress } from '@/lib/scenarioCoverageStore';

/**
 * Compact loading animation with real download progress for scenario coverage data.
 * Shows a patriotic flag animation (AHP) or matrix-style loader (Luigi's)
 * with a progress bar driven by actual download percentage.
 *
 * Lives in src/lib/ because src/components/scenarios/ is not writable by amh-dev.
 */
export function CoverageProgressLoader() {
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';
  const { isDark } = useTheme();
  const { progress } = useScenarioCoverageProgress();

  const stars = useMemo(() => {
    const positions: { x: number; y: number; delay: number }[] = [];
    for (let row = 0; row < 5; row++) {
      const cols = row % 2 === 0 ? 4 : 3;
      const offsetX = row % 2 === 0 ? 4 : 12;
      for (let col = 0; col < cols; col++) {
        positions.push({
          x: offsetX + col * 22,
          y: 8 + row * 20,
          delay: Math.random() * 2,
        });
      }
    }
    return positions;
  }, []);

  if (!isAhp) {
    // Luigi's brand: compact matrix-style loader with progress
    return (
      <div className="flex flex-col items-center gap-3 py-6">
        <div className="relative w-32 h-16">
          <div
            className="absolute inset-x-0 h-0.5 rounded-full"
            style={{
              background: '#00ff41',
              boxShadow: '0 0 8px #00ff41',
              animation: 'cpMatrixScan 2s ease-in-out infinite',
            }}
          />
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full"
              style={{
                width: 3,
                height: 3,
                left: `${(i % 5) * 20 + 10}%`,
                top: `${Math.floor(i / 5) * 25 + 10}%`,
                backgroundColor: '#00ff41',
                opacity: 0,
                animation: `cpMatrixDotFade 2.5s ease-in-out ${i * 0.06}s infinite`,
              }}
            />
          ))}
        </div>
        {/* Progress bar */}
        <div className="w-40 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300"
            style={{
              width: `${progress > 0 ? progress : 5}%`,
              backgroundColor: '#00ff41',
              boxShadow: '0 0 6px #00ff41',
            }}
          />
        </div>
        <div className="text-center">
          <p className="font-mono text-xs" style={{ color: '#00ff41', textShadow: '0 0 6px rgba(0,255,65,0.4)' }}>
            {progress > 0 ? `DOWNLOADING ${progress}%` : 'LOADING DATA'}
          </p>
          <p className="text-[10px] text-muted-foreground mt-1">
            First visit — cached for future use
          </p>
        </div>
        <style>{`
          @keyframes cpMatrixScan {
            0%, 100% { top: 0%; opacity: 0.3; }
            50% { top: 85%; opacity: 1; }
          }
          @keyframes cpMatrixDotFade {
            0%, 100% { opacity: 0.1; transform: scale(0.5); }
            50% { opacity: 1; transform: scale(1.2); }
          }
        `}</style>
      </div>
    );
  }

  // AHP: compact patriotic loader with real progress
  const navy = isDark ? '#1a3a6e' : '#002868';
  const red = '#B22234';
  const white = isDark ? '#FAF9F6' : '#FFFFFF';
  const gold = '#C5A55A';
  const textCol = isDark ? '#FAF9F6' : '#1B1B1B';
  const mutedCol = isDark ? 'rgba(250,249,246,0.5)' : 'rgba(0,0,0,0.4)';

  return (
    <div className="flex flex-col items-center gap-3 py-6">
      {/* Small flag */}
      <div
        className="relative overflow-hidden rounded-md shadow-lg"
        style={{
          width: 140,
          height: 82,
          animation: 'cpFlagFloat 3s ease-in-out infinite',
        }}
      >
        {/* Stripes */}
        {Array.from({ length: 13 }).map((_, i) => (
          <div
            key={`s-${i}`}
            className="absolute left-0 right-0"
            style={{
              top: `${(i / 13) * 100}%`,
              height: `${100 / 13}%`,
              backgroundColor: i % 2 === 0 ? red : white,
              animation: `cpStripeWave 2.5s ease-in-out ${i * 0.08}s infinite`,
              transformOrigin: 'left center',
            }}
          />
        ))}
        {/* Canton */}
        <div
          className="absolute top-0 left-0 z-10"
          style={{
            width: '42%',
            height: `${(7 / 13) * 100}%`,
            backgroundColor: navy,
            animation: 'cpStripeWave 2.5s ease-in-out 0s infinite',
            transformOrigin: 'left center',
          }}
        >
          <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
            {stars.map((star, i) => (
              <text
                key={i}
                x={star.x + 2}
                y={star.y + 6}
                fill={gold}
                fontSize="8"
                textAnchor="middle"
                dominantBaseline="middle"
                style={{
                  opacity: 0,
                  animation: `cpStarTwinkle 2s ease-in-out ${star.delay}s infinite`,
                }}
              >
                ★
              </text>
            ))}
          </svg>
        </div>
        {/* Shimmer */}
        <div
          className="absolute inset-0 z-20 pointer-events-none"
          style={{
            background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.15) 50%, transparent 70%)',
            animation: 'cpFlagShimmer 3s ease-in-out infinite',
          }}
        />
      </div>

      {/* Real progress bar */}
      <div className="w-40 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)' }}>
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{
            width: `${progress > 0 ? progress : 5}%`,
            backgroundColor: gold,
            boxShadow: `0 0 6px ${gold}`,
          }}
        />
      </div>

      {/* Text */}
      <div className="text-center">
        <p
          className="text-sm font-medium"
          style={{
            color: textCol,
            fontFamily: "'Instrument Serif', Georgia, serif",
          }}
        >
          Loading hospital pricing data{progress > 0 ? ` ${progress}%` : '...'}
        </p>
        <p className="text-[10px] mt-0.5" style={{ color: mutedCol }}>
          First visit — cached for future use
        </p>
      </div>

      <style>{`
        @keyframes cpFlagFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-4px); }
        }
        @keyframes cpStripeWave {
          0%, 100% { transform: scaleX(1) skewY(0deg); }
          25% { transform: scaleX(1.01) skewY(0.5deg); }
          75% { transform: scaleX(0.99) skewY(-0.5deg); }
        }
        @keyframes cpStarTwinkle {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.1); }
        }
        @keyframes cpFlagShimmer {
          0% { transform: translateX(-120%); }
          60%, 100% { transform: translateX(120%); }
        }
      `}</style>
    </div>
  );
}
